/**
 * Advance Receipts and Adjustments Table Component
 * Displays advance payment receipts with adjustment details
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import NewFeeReciept from '../../finance/newFeeReciept';
import LoadingComponent from '../../loadingComponent';

export class AdvanceReceiptsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            advanceReceipts: [],
            flattenedData: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: null,
            sourceDialogVisible: false,
            sourceDialogData: []
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        
        // Bind methods
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    componentDidMount() {
        console.log('AdvanceReceiptsTable component mounted');
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            this.setState({ isLoading: false, advanceReceipts: [], flattenedData: [] });
        }
    }

    processTransactionsData(data) {
        console.log('AdvanceReceiptsTable - Processing transactions data:', data);

        const collections = data?.collections || [];

        if (collections && collections.length > 0) {
            // Filter for advance transactions (fee type contains "advance" OR payment mode is "Adjustment")
            const advanceReceipts = [];

            collections.forEach(transaction => {
                const feePaidDetails = transaction.feePaidDetails;
                const paymentMode = transaction.paymentMode;

                // Check if payment mode is "Adjustment"
                const isAdjustmentPayment = paymentMode && paymentMode.toLowerCase() === 'adjustment';

                if (feePaidDetails && Array.isArray(feePaidDetails)) {
                    // Check if any feePaidDetail has feeType containing "Advance" OR if payment mode is "Adjustment"
                    const advanceDetails = feePaidDetails.filter(detail =>
                        (detail.feeType && detail.feeType.toLowerCase().includes('advance')) || isAdjustmentPayment
                    );

                    if (advanceDetails.length > 0) {
                        // Calculate totals for this receipt
                        const advanceReceivedAmount = advanceDetails.reduce((sum, detail) =>
                            sum + (detail.amountPaying || 0), 0);

                        advanceReceipts.push({
                            ...transaction,
                            advanceDetails: advanceDetails,
                            advanceReceivedAmount: advanceReceivedAmount
                        });
                    }
                }
            });

            console.log('Filtered advance receipts:', advanceReceipts.length);

            const flattened = this.flattenReceipts(advanceReceipts);
            console.log('AdvanceReceiptsTable - Flattened Data:', flattened);

            this.setState({
                advanceReceipts: advanceReceipts,
                flattenedData: flattened,
                isLoading: false
            });
        } else {
            console.log('AdvanceReceiptsTable - No collections in data');
            this.setState({
                advanceReceipts: [],
                flattenedData: [],
                isLoading: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            this.setState({ isLoading: false, advanceReceipts: [], flattenedData: [] });
        }
    }

    flattenReceipts = (receipts) => {
        const flattened = [];

        receipts.forEach((receipt, receiptIndex) => {
            const advanceDetails = receipt.advanceDetails || [];
            const totalRows = advanceDetails.length;

            advanceDetails.forEach((detail, detailIndex) => {
                flattened.push({
                    sNo: receiptIndex + 1,
                    receipt: receipt,
                    detail: detail,
                    receiptIndex: receiptIndex,
                    detailIndex: detailIndex,
                    isFirstRow: detailIndex === 0,
                    isLastRow: detailIndex === totalRows - 1,
                    totalRowSpan: totalRows,
                    uniqueKey: `${receipt._id}-${detailIndex}`
                });
            });
        });

        return flattened;
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    transactionIdBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        const receipt = rowData.receipt;
        return (
            <div className="transaction-info">
                <div className="transaction-id">{receipt.transctionId || "N/A"}</div>
                <div className="receipt-number">Receipt: {receipt.receiptNo}</div>
            </div>
        );
    };

    receiptDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{this.formatDate(rowData.receipt.receiptDate || rowData.receipt.transactionDate)}</span>;
    };

    academicYearBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';

        return <span>{rowData?.receipt?.academicCode || 'N/A'}</span>;
    };

    modeBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>{rowData.receipt.paymentMode || 'N/A'}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        const detail = rowData.detail;
        let feeTypeDisplay = detail.feeType || 'N/A';
        if (detail.paymentTerm && detail.paymentTerm !== 'undefined') {
            feeTypeDisplay = `${feeTypeDisplay} - ${detail.paymentTerm}`;
        }
        return <span>{feeTypeDisplay}</span>;
    };

    advanceReceivedBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return <span>₹{rowData.receipt.advanceReceivedAmount?.toFixed(2) || '0.00'}</span>;
    };

    actionsBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        return (
            <div className="action-buttons flex flex-column justify-content-start align-items-center gap-2">
                <Button
                    label="Print"
                    className="p-button-sm view-btn"
                    onClick={() => {
                        console.log('Print receipt:', rowData.receipt);
                        this.setState({
                            isEnableReceipt: true,
                            receiptData: rowData.receipt
                        });
                    }}
                />
            </div>
        );
    };

    sourceBodyTemplate = (rowData) => {
        if (!rowData.isFirstRow) return '';
        const sources = rowData.receipt?.advanceSourceReceipts;
        if (!sources || sources.length === 0) return <span style={{ color: '#888', fontSize: '12px' }}>-</span>;
        return (
            <Button
                label={`View (${sources.length})`}
                className="p-button-sm p-button-outlined p-button-info"
                onClick={() => this.setState({ sourceDialogVisible: true, sourceDialogData: sources })}
            />
        );
    };

    rowClassName = (rowData) => {
        const classes = [];

        if (rowData.isFirstRow) {
            classes.push('transaction-group-first-row');
        }

        if (rowData.isLastRow) {
            classes.push('transaction-group-last-row');
        }

        return classes.join(' ');
    };

    calculateTotals = () => {
        const { advanceReceipts } = this.state;

        let totalAdvance = 0;
        let totalAdjustment = 0;

        advanceReceipts.forEach(receipt => {
            // Check if this is an adjustment payment
            const isAdjustment = receipt.paymentMode &&
                receipt.paymentMode.toLowerCase() === 'adjustment';

            if (isAdjustment) {
                totalAdjustment += receipt.advanceReceivedAmount || 0;
            } else {
                totalAdvance += receipt.advanceReceivedAmount || 0;
            }
        });

        const remainingAdvance = totalAdvance - totalAdjustment;

        return { totalAdvance, totalAdjustment, remainingAdvance };
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const { totalAdvance, totalAdjustment, remainingAdvance } = this.calculateTotals();

        return (
            <>
                <div className="advance-transactions-table-container ">
                    {isLoading && <LoadingComponent />}


                    {!isLoading && (
                        <div className="transactions-table-container">
                            <div className="info-note" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb', color: '#155724' }}>
                                <strong>Total Advance:</strong> ₹{totalAdvance.toFixed(2)} |
                                <strong> Total Adjustment:</strong> ₹{totalAdjustment.toFixed(2)} |
                                <strong> Remaining Advance:</strong> ₹{remainingAdvance.toFixed(2)}
                            </div>
                            <div className="transactions-table-wrapper">
                                <div ref={this.dataScrollRef}>
                                    <DataTable
                                        value={flattenedData}
                                        scrollable
                                        scrollHeight="flex"
                                        className="transactions-table"
                                        showGridlines
                                        rowClassName={this.rowClassName}
                                        emptyMessage="No advance receipts found"
                                    >
                                        <Column
                                            header="S.No"
                                            body={this.snoBodyTemplate}
                                            headerClassName="sno-header"
                                            bodyClassName="sno-cell"
                                            style={{ width: '80px', minWidth: '80px', textAlign: 'center' }}
                                        />
                                        <Column
                                            header="TransactionID/Receipt No"
                                            body={this.transactionIdBodyTemplate}
                                            headerClassName="transaction-id-header"
                                            bodyClassName="transaction-id-cell"
                                            style={{ width: '220px', minWidth: '220px' }}
                                        />
                                        <Column
                                            header="Receipt Date"
                                            body={this.receiptDateBodyTemplate}
                                            headerClassName="receipt-date-header"
                                            bodyClassName="receipt-date-cell"
                                            style={{ width: '130px', minWidth: '130px' }}
                                        />
                                        <Column
                                            header="Academic Year"
                                            body={this.academicYearBodyTemplate}
                                            headerClassName="academic-year-header"
                                            bodyClassName="academic-year-cell"
                                            style={{ width: '140px', minWidth: '140px' }}
                                        />
                                        <Column
                                            header="Mode"
                                            body={this.modeBodyTemplate}
                                            headerClassName="mode-header"
                                            bodyClassName="mode-cell"
                                            style={{ width: '150px', minWidth: '150px' }}
                                        />
                                        <Column
                                            header="Fee Type"
                                            body={this.feeTypeBodyTemplate}
                                            headerClassName="fee-type-header"
                                            bodyClassName="fee-type-cell"
                                            style={{ width: '280px', minWidth: '280px' }}
                                        />
                                        <Column
                                            header="Advance received amount"
                                            body={this.advanceReceivedBodyTemplate}
                                            headerClassName="advance-received-header"
                                            bodyClassName="advance-received-cell"
                                            style={{ width: '200px', minWidth: '200px', textAlign: 'right' }}
                                        />
                                        <Column
                                            header="Source Receipts"
                                            body={this.sourceBodyTemplate}
                                            headerClassName="source-header"
                                            bodyClassName="source-cell"
                                            style={{ width: '140px', minWidth: '140px', textAlign: 'center' }}
                                        />
                                        <Column
                                            header="Actions"
                                            body={this.actionsBodyTemplate}
                                            headerClassName="actions-header"
                                            bodyClassName="actions-cell"
                                            style={{ width: '150px', minWidth: '150px', textAlign: 'center' }}
                                        />
                                    </DataTable>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <Dialog
                    visible={this.state.sourceDialogVisible}
                    onHide={() => this.setState({ sourceDialogVisible: false, sourceDialogData: [] })}
                    header="Adjustment Source Breakdown"
                    style={{ width: '700px' }}
                >
                    <DataTable
                        value={this.state.sourceDialogData}
                        emptyMessage="No source receipts found"
                        showGridlines
                    >
                        <Column field="receiptNo" header="Receipt No" />
                        <Column field="paymentMode" header="Payment Mode" />
                        <Column field="feeType" header="Fee Type" />
                        <Column
                            header="Date"
                            body={(row) => row.transactionDate ? new Date(row.transactionDate).toLocaleDateString('en-IN') : '-'}
                        />
                        <Column
                            header="Amount"
                            body={(row) => `₹${(row.contributionAmount || 0).toFixed(2)}`}
                        />
                    </DataTable>
                </Dialog>

                {this.state.isEnableReceipt && (
                    <NewFeeReciept
                        receiptData={this.state?.receiptData}
                        hideFeeReciept={() => {
                            this.setState({
                                isEnableReceipt: false,
                                receiptData: null
                            });
                        }}
                    />
                )}
            </>
        );
    }
}
